import { useEffect, useState, type FormEvent } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { paths, type PortalContact } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { call } from "../lib/callFunction";

// Customer page → Customer portal: the people at this customer who may sign
// in (their email gets a link, their cell gets a code). Invite, re-send, or
// take someone off; their signed-in devices are out right away.

type Row = PortalContact & { id: string };
const day = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

export function PortalAccess({ tenantId, customerId, defaultEmail, defaultPhone }: { tenantId: string; customerId: string; defaultEmail: string; defaultPhone: string }) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", send: true });
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [confirmOff, setConfirmOff] = useState<string | null>(null);

  useEffect(
    () =>
      onSnapshot(
        query(collection(db, paths.tenantPortalContacts(tenantId)), where("customerId", "==", customerId)),
        (s) => setRows(s.docs.map((d) => ({ id: d.id, ...(d.data() as PortalContact) })).sort((a, b) => Number(a.status === "revoked") - Number(b.status === "revoked") || a.createdAt - b.createdAt)),
        () => setRows([]),
      ),
    [tenantId, customerId],
  );

  async function act(key: string, fn: () => Promise<string>) {
    setBusy(key);
    setMsg(null);
    try {
      setMsg({ ok: true, text: await fn() });
    } catch (err) {
      setMsg({ ok: false, text: (err as Error).message });
    } finally {
      setBusy(null);
    }
  }
  const save = (e: FormEvent) => {
    e.preventDefault();
    void act("save", async () => {
      const r = await call<{ emailed: boolean }>("invitePortalContact", { tenantId, customerId, ...(editing ? { contactId: editing } : {}), ...form });
      setAdding(false);
      setEditing(null);
      return r.emailed ? `Emailed a sign-in link to ${form.email}.` : form.email && form.send ? "Saved, but the email didn't go out. Try Send link again." : "Saved. They sign in at the portal with their cell number.";
    });
  };
  const resend = (r: Row) =>
    act(`send:${r.id}`, async () => {
      const res = await call<{ emailed: boolean }>("invitePortalContact", { tenantId, customerId, contactId: r.id, name: r.name, email: r.email, phone: r.phone, send: true });
      return res.emailed ? `Emailed a new sign-in link to ${r.email}.` : "The email didn't go out. Try again in a minute.";
    });
  const revoke = (r: Row) =>
    act(`off:${r.id}`, async () => {
      await call("revokePortalContact", { tenantId, contactId: r.id });
      setConfirmOff(null);
      return `${r.name || r.email || r.phone} can't sign in anymore.`;
    });

  if (rows === null) return null;
  const portalUrl = `${window.location.origin}/portal`;
  const formOpen = adding || editing !== null;
  return (
    <Card className="p-0">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-3">
        <CardTitle className="mb-0">Customer portal</CardTitle>
        {!formOpen ? (
          <Button
            variant="ghost"
            className="px-3 py-1 text-xs"
            onClick={() => {
              setForm({ name: "", email: rows.some((r) => r.email === defaultEmail.toLowerCase()) ? "" : defaultEmail, phone: "", send: true });
              setAdding(true);
              setMsg(null);
            }}
          >
            Let someone in
          </Button>
        ) : null}
      </div>
      <p className="px-4 pt-1 text-xs text-steel">
        People here see this customer's invoices (and pay them), estimates (and sign them), your W-9 and certificates, their jobs, and can send you POs. They sign in at{" "}
        <span className="text-ink">{portalUrl}</span> with a link to their email or a code to their cell.
      </p>

      {formOpen ? (
        <form onSubmit={save} className="grid gap-2 border-b border-steel/10 px-4 py-2 sm:grid-cols-3">
          <div>
            <Label>Name</Label>
            <Input className="py-1 text-sm" value={form.name} maxLength={120} placeholder="Pat (purchasing)" onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label>Email</Label>
            <Input className="py-1 text-sm" type="email" value={form.email} maxLength={200} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <Label>Cell (for a texted code)</Label>
            <Input className="py-1 text-sm" type="tel" value={form.phone} maxLength={40} placeholder={defaultPhone || "(602) 555-0100"} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <label className="flex items-center gap-2 text-xs text-ink sm:col-span-2">
            <input type="checkbox" checked={form.send} disabled={!form.email} onChange={(e) => setForm({ ...form, send: e.target.checked })} />
            Email them a sign-in link now
          </label>
          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="ghost" className="px-3 py-1 text-xs" onClick={() => { setAdding(false); setEditing(null); }}>
              Cancel
            </Button>
            <Button type="submit" className="px-3 py-1 text-xs" busy={busy === "save"} disabled={!form.email.trim() && !form.phone.trim()}>
              {editing ? "Save" : form.send && form.email ? "Let in and email" : "Let in"}
            </Button>
          </div>
        </form>
      ) : null}

      <div className="mt-1">
        {rows.map((r) => (
          <div key={r.id} className={cn("flex min-h-9 flex-wrap items-center gap-2 border-b border-steel/10 px-4 py-1.5 text-sm last:border-0", r.status === "revoked" && "opacity-60")}>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-ink">{r.name || r.email || r.phone}</span>
              <span className="block truncate text-xs text-steel">
                {[r.name ? r.email : "", r.phone].filter(Boolean).join(" · ")}
                {r.status === "revoked"
                  ? ` · taken off ${r.revokedAt ? day(r.revokedAt) : ""}`
                  : r.lastSignInAt
                    ? ` · last in ${day(r.lastSignInAt)}`
                    : r.invitedAt
                      ? ` · link sent ${day(r.invitedAt)}, not in yet`
                      : " · not in yet"}
              </span>
            </span>
            {r.status === "active" ? (
              confirmOff === r.id ? (
                <span className="flex items-center gap-2 text-xs">
                  Take them off?
                  <button className="font-semibold text-danger hover:underline" disabled={busy !== null} onClick={() => void revoke(r)}>
                    Take off
                  </button>
                  <button className="text-steel hover:text-ink" onClick={() => setConfirmOff(null)}>
                    Keep
                  </button>
                </span>
              ) : (
                <span className="flex shrink-0 items-center gap-3 text-xs">
                  {r.email ? (
                    <button className="text-steel underline hover:text-ink" disabled={busy !== null} onClick={() => void resend(r)}>
                      {busy === `send:${r.id}` ? "Sending…" : "Send link"}
                    </button>
                  ) : null}
                  <button
                    className="text-steel underline hover:text-ink"
                    onClick={() => {
                      setForm({ name: r.name, email: r.email, phone: r.phone, send: false });
                      setEditing(r.id);
                      setAdding(false);
                      setMsg(null);
                    }}
                  >
                    Edit
                  </button>
                  <button className="text-steel hover:text-danger" onClick={() => setConfirmOff(r.id)}>
                    Take off
                  </button>
                </span>
              )
            ) : (
              <button
                className="shrink-0 text-xs text-steel underline hover:text-ink"
                onClick={() => {
                  setForm({ name: r.name, email: r.email, phone: r.phone, send: !!r.email });
                  setEditing(r.id);
                  setMsg(null);
                }}
              >
                Let back in
              </button>
            )}
          </div>
        ))}
        {rows.length === 0 && !formOpen ? <p className="px-4 pb-3 text-xs text-steel">Nobody yet.</p> : null}
      </div>
      {msg ? <p className={cn("px-4 pb-3 text-xs", msg.ok ? "text-success" : "text-danger")}>{msg.text}</p> : null}
    </Card>
  );
}
